import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Command as CommandPrimitive } from "cmdk";
import { Building2, Handshake, Search, User } from "lucide-react";
import { useDataProvider } from "ra-core";
import { useLocation, useNavigate } from "react-router";

import {
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

import type { CrmDataProvider, SearchResult } from "../providers/types";

const GROUPS = [
  { kind: "contact", heading: "Contacts", Icon: User },
  { kind: "company", heading: "Companies", Icon: Building2 },
  { kind: "deal", heading: "Deals", Icon: Handshake },
] as const;

/** `value` once it has stopped changing for `ms`. */
function useSettled(value: string, ms = 200) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(handle);
  }, [value, ms]);
  return settled;
}

/**
 * The top bar's search (W12 U1): a button that looks like a field, and a ⌘K /
 * Ctrl+K palette over GET /search. Picking a match opens its page.
 */
export function SearchCommand() {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const settled = useSettled(term.trim());
  const { pathname } = useLocation();

  // Any navigation (a link, the back button) closes the palette.
  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const results = useQuery({
    queryKey: ["search", settled],
    queryFn: () => dataProvider.search(settled),
    enabled: open && settled.length >= 2,
  });

  const go = (result: SearchResult) => {
    setOpen(false);
    setTerm("");
    navigate(result.href);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-9 w-full max-w-md items-center gap-2 rounded-md border bg-background px-3 text-sm text-muted-foreground shadow-xs transition-colors hover:bg-accent hover:text-accent-foreground"
      >
        <Search className="size-4" />
        <span className="flex-1 text-left">
          Search contacts, companies, deals…
        </span>
        <kbd className="hidden rounded border bg-muted px-1.5 font-mono text-[10px] font-medium sm:inline">
          ⌘K
        </kbd>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="overflow-hidden p-0 sm:max-w-xl">
          <DialogTitle className="sr-only">Search</DialogTitle>
          <DialogDescription className="sr-only">
            Find a contact, company or deal by name, email, phone or NMLS.
          </DialogDescription>
          <CommandPrimitive
            shouldFilter={false}
            className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
          >
            <CommandInput
              value={term}
              onValueChange={setTerm}
              placeholder="Name, email, phone or NMLS"
            />
            <CommandList>
              {settled.length >= 2 && !results.isPending ? (
                <CommandEmpty>No matches.</CommandEmpty>
              ) : null}
              {GROUPS.map(({ kind, heading, Icon }) => {
                const rows = (results.data ?? []).filter(
                  (r) => r.kind === kind,
                );
                if (!rows.length) return null;
                return (
                  <CommandGroup key={kind} heading={heading}>
                    {rows.map((row) => (
                      <CommandItem
                        key={row.id}
                        value={`${kind}-${row.id}`}
                        onSelect={() => go(row)}
                      >
                        <Icon className="text-muted-foreground" />
                        <span className="truncate">{row.label}</span>
                        {row.detail ? (
                          <span className="ml-auto truncate text-xs text-muted-foreground">
                            {row.detail.replace(/_/g, " ")}
                          </span>
                        ) : null}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                );
              })}
            </CommandList>
          </CommandPrimitive>
        </DialogContent>
      </Dialog>
    </>
  );
}
